/**
 * Waar het daglicht vandaan komt: de zon, de daklichtstraten en de dockdeuren.
 *
 * Deze maten staan bij elkaar omdat ze niet los van elkaar kloppen. Een
 * daklicht moet zo liggen dat zijn zonvlek ín een gangpad valt en niet op een
 * stellingrij, en dat hangt af van de zonnestand. Verschuift de zon, dan
 * verschuiven de daklichten mee — vandaar dat de rijen hier uit de zon worden
 * afgeleid in plaats van als losse getallen in `Dak.tsx` te staan.
 */
import * as THREE from 'three'
import { HAL, type Indeling } from './indeling'
import { rondOm } from './omloop'

/**
 * Richting náár de zon.
 *
 * Bijna langs de gangpaden (de x-as), en dat is de enige stand die werkt. De
 * stellingen zijn 7,6 m hoog en de gangpaden 3,4 m breed: komt de zon meer dan
 * ~24° dwars op de gangpaden binnen, dan valt elke zonvlek op de stellingrij
 * ernaast en bereikt geen straal de vloer. Langs de gang kan hij wél tot op de
 * grond, en dan hellen de lichtschachten in de lengte van het gangpad — precies
 * de kijkrichting van het hoofdscherm.
 *
 * Hij staat op de −x-kant, dus vóór de hoofdcamera. Stof verstrooit licht
 * vooral vooruit: een schacht waar je tegenin kijkt gloeit, een schacht met de
 * zon in je rug is bijna onzichtbaar.
 */
export const ZON = new THREE.Vector3(-0.6, 1, 0.12).normalize()

/** Hoogte van de onderkant van de daklichten. */
export const DAKLICHT_Y = HAL.hoogte - 0.02
export const DAKLICHT_BREEDTE = 1.6

/** Gordingen liggen om de 4,5 m vanaf −0,8 × de halbreedte (zie `Plafond.tsx`). */
const GORDING_START = -HAL.breedte * 0.8
const GORDING_AFSTAND = 4.5

export interface Daklicht {
  /** Midden van het daklicht. */
  x: number
  z: number
  lengte: number
  breedte: number
}

export interface DaklichtRij {
  z: number
  /** Eerste en laatste x van de rij, voor de lichtschacht die de hele rij volgt. */
  x0: number
  x1: number
  vakken: Daklicht[]
}

/**
 * Eén rij daklichten per gangpad, zo verschoven dat de zonvlek midden in het
 * gangpad op de vloer valt. De vakken liggen tussen de gordingen in, zoals
 * een echte lichtstraat tussen de dakliggers is gemonteerd — de schaduw van de
 * gordingen snijdt dan vanzelf de pauzes in de schacht.
 */
export function daklichtRijen(ind: Indeling): DaklichtRij[] {
  // Zoveel schuift een straal op van dak tot vloer.
  const dx = (-ZON.x / ZON.y) * DAKLICHT_Y
  const dz = (-ZON.z / ZON.y) * DAKLICHT_Y

  // De vlekken moeten binnen de stellingen blijven: daarbuiten staat niets om
  // ze op te vangen en lezen ze als een fout in het dak.
  const halveStelling = (ind.cfg.bays * 2.7) / 2
  const vlekMin = -halveStelling + 1.5
  const vlekMax = halveStelling - 1.5

  const rijen: DaklichtRij[] = []
  for (let i = 0; i < ind.cfg.gangpaden; i++) {
    const z = ind.gangpadZ(i) - dz
    const vakken: Daklicht[] = []
    for (let g = GORDING_START; g + GORDING_AFSTAND <= HAL.breedte * 0.8 + 0.01; g += GORDING_AFSTAND) {
      const a = g + 0.3
      const b = g + GORDING_AFSTAND - 0.3
      if (a + dx < vlekMin || b + dx > vlekMax) continue
      vakken.push({ x: (a + b) / 2, z, lengte: b - a, breedte: DAKLICHT_BREEDTE })
    }
    if (vakken.length === 0) continue
    rijen.push({
      z,
      x0: vakken[0].x - vakken[0].lengte / 2,
      x1: vakken[vakken.length - 1].x + vakken[vakken.length - 1].lengte / 2,
      vakken,
    })
  }
  return rijen
}

/** De vier dockdeuren in de achterwand. Staat ook in `Props.tsx`, dat ze bouwt. */
export const DOCKS_X = [-18, -6, 6, 18]
export const DOCK_Z = -HAL.diepte * 0.88

/** Een deur gaat in ±52 s open en dicht: vier keer per omloop van de hal (`omloop.ts`). */
const DEUR = rondOm(0.12)

/**
 * Hoe ver een dockdeur openstaat, 0–1, op `tijd` van de klok van de hal. De
 * deuranimatie in `Props.tsx` gebruikt hem ook: het daglicht dat binnenvalt
 * hoort mee te gaan met de deur, anders schijnt er licht door een dichte
 * roldeur.
 */
export function dockOpen(tijd: number, index: number) {
  const t = tijd * DEUR + index * 2.1
  return (Math.sin(t) + 1) / 2
}
